// css for gradient color of the mobile icon
import './mobileIcon.css';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {TriggerButton} from '../../components/sideBar/sideBarMenu';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCheck} from '@fortawesome/free-solid-svg-icons';
import {useTheme} from '@mui/material/styles';
import {AppBar, Box, Toolbar, Paper, Typography, Stack, Slide, Button, ListItemIcon, Grid} from '@mui/material';
import {useMediaQuery} from '@mui/material';
import {useContext, useEffect, useMemo, useRef, useState} from 'react';
import MiscContext from '../../context/MiscContext';
import {mainLinearGradient} from '../../theme/gradients';
import mainLogo from '../../images/logo_black_letters_2.png';
import InformativeDialog from '../dialogbox/informativeDialog';
import {useActivityClickApi} from '../../api/activityClick';
import {paymentsEnabled} from '../../constants';
import {PricingPlansPopup} from '../pricingOptions/pricingPlansPopup';

/**
 * CustomDivider
 * Horizontal Line that separates each page name point in the Header of FormBase
 *
 * @param {Object} props - Component props
 * @param {string} props.mainColor - Acceptable color code string for the background color of the line
 * @param {string} props.progressColor - Acceptable color code string for the bar signaling the progress
 * @param {number} props.progress - Number from 0 to 100 representing the percentage of the horizontal line covered by the progress color
 *
 * @returns {React.Component} Rendered Component
 */
const CustomDivider = ({mainColor, progressColor, progress}) => {
  const height = '6px';
  const borderRadius = 30;
  const gradientOrNotProps = {
    ...(progressColor.startsWith('linear-gradient') ? {background: progressColor} : {backgroundColor: progressColor}),
  };
  return (
    <Box
      sx={{
        width: '100%',
        height: height,
        backgroundColor: mainColor,
        borderRadius: borderRadius,
      }}
    >
      <Box
        sx={{
          width: `${progress}%`,
          height: height,
          borderRadius: borderRadius,
          ...gradientOrNotProps,
        }}
      ></Box>
    </Box>
  );
};

/**
 * CustomDivider
 * Horizontal Line that represents the whole progress of a FormBase, not just the progress between 2 page names. This is intended for mobile screen sizes
 *
 * @param {Object} props - Component props
 * @param {string} props.mainColor - Acceptable color code string for the background color of the line
 * @param {string} props.progressColor - Acceptable color code string for the bar signaling the progress
 * @param {number} props.progress - Number from 0 to 100 representing the percentage of the horizontal line covered by the progress color
 *
 * @returns {React.Component} Rendered Component
 */
const ResponsiveBar = ({mainColor, progressColor, progress}) => {
  const height = '15px';
  const borderRadius = 30;
  const gradientOrNotProps = {
    ...(progressColor.startsWith('linear-gradient') ? {background: progressColor} : {backgroundColor: progressColor}),
  };
  if (progress < 0) progress = 0;
  return (
    <Box
      sx={{
        width: '100%',
        height: height,
        backgroundColor: mainColor,
        borderRadius: borderRadius,
      }}
    >
      <Box
        sx={{
          width: `${progress}%`,
          height: height,
          borderRadius: borderRadius,
          ...gradientOrNotProps,
        }}
      ></Box>
    </Box>
  );
};

/**
 * StatusCircle
 * Little Circle that includes the page name below to represent a point of a page name in the Header of a FormBase
 *
 * @param {Object} props - Component props
 * @param {string} props.text - Ideally the page name of that circle, it could be any text
 * @param {Number} props.index - Index of this page name(NOT THE PAGE NAME YOU ARE BUT THE ONE FROM THAT TEXT)
 * @param {Number} prop.currentIndex - Index of the page name the user currently is. This is used to determine the styling  based on if the props.index is before or after this one.
 *
 * @returns {React.Component} Rendered Component
 */
const StatusCircle = ({text, index, currentIndex}) => {
  const theme = useTheme();

  return (
    <Stack alignItems="center" textAlign="center" key={index} sx={{maxWidth: 35}}>
      <Box
        sx={{
          height: 34,
          width: 34,
          backgroundColor: currentIndex >= index ? theme.palette.primary.main : 'transparent',
          boxSizing: 'border-box',
          border: currentIndex >= index ? 'none' : `3px solid ${theme.palette.primary.main}`,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {currentIndex > index ? (
          <Box>
            <FontAwesomeIcon icon={faCheck} size="lg" style={{color: theme.palette.neutral.main}} />
          </Box>
        ) : (
          <Typography
            color={'primary.main'}
            fontSize={18.5}
            fontWeight={600}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {currentIndex >= index ? '' : index + 1}
          </Typography>
        )}
      </Box>
      <Typography
        color={currentIndex >= index ? 'primary.main' : 'gray'}
        sx={{
          mt: 2,
          width: {
            xs: 'fit-content',
            sm: '200px !important',
          },
          fontSize: {
            xs: 14,
            sm: 16,
          },
        }}
      >
        {text}
      </Typography>
    </Stack>
  );
};

/**
 * Header
 * Top section of the FormBase component. It will display a progress bar, information about the users plan and a sidebar.
 *
 * @param {Object} props - Component props
 * @param {string[]} props.pageNames - Array of names ordered in the flow they will be shown
 * @param {Number} props.pageNumber - Current index in the pageNames array
 * @param {Number} props.currentPageProgress - The progress shown is rendered based on the current index you are, however you can modify the progress between the current page you are and the next one if you want, otherwise it will only show a default value.Example, instead of being this (*)-----(*)---  (*). You can pass a number to change the last part: (*)-----(*)-    (*)
 * @param {boolean} props.useCurrentPageProgress - Boolean to specify if you want to use the currentPageProgress prop, it is only useful for mobile screen sizes when sometimes you want to show the default one even though you calculated a progress
 *
 * @returns {React.Component} Rendered Component
 */
const Header = ({pageNumber, pageNames, currentPageProgress, useCurrentPageProgress, pageTitle}) => {
  const {userPlan} = useContext(MiscContext);
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const possibleProgress = useMemo(() => {
    let theProgress = ((pageNumber + 1) / pageNames.length) * 100;
    if (theProgress > 100) theProgress = 100;
    return theProgress;
  }, [pageNumber, pageNames]);
  const [informativeDialog, setInformativeDialog] = useState({
    title: '',
    subTitle: '',
    isOpen: false,
    width: 'sm',
  });
  const [paymentsPopupOpen, setPaymentsPopupOpen] = useState(false);
  const activityClickApi = useActivityClickApi();

  const handleUpgradeClick = async () => {
    //Save in DB click activity of corresponding button
    activityClickApi.postActivityClick('premium_button');

    if (!paymentsEnabled) {
      //open modal popup (this for first released which does not include premium version)
      setInformativeDialog({
        title: 'Coming soon',
        subTitle: `Our premium version has not been released, we'll update you when it is!`,
        isOpen: true,
        width: 'sm',
        onConfirm: () => {
          setInformativeDialog(prev => {
            return {...prev, isOpen: false};
          });
        },
      });
      return;
    }
    setPaymentsPopupOpen(true);
  };

  return (
    <Box
      sx={{
        position: 'sticky',
        zIndex: 300,
        top: 0,
      }}
    >
      {!isSmallScreen && (
        <Box
          sx={{
            mx: 2,
            backgroundColor: 'white',
            boxSizing: 'border-box',
            boxShadow: '0px 14px 65px -40px rgba(0,0,0,0.69)',
          }}
        >
          <AppBar position="static" sx={{backgroundColor: 'white', pt: 1}} elevation={0}>
            <Toolbar sx={{justifyContent: 'end'}}>
            
              <TriggerButton
                iconButtonStyles={{
                  mr: 2,
                }}
              />
            </Toolbar>
          </AppBar>
        </Box>
      )}
      {!isSmallScreen ? (
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="start"
          spacing={2}
          sx={{
            p: 3,
            py: 1,
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: 'white',
          }}
        >
          {pageNames
            .map((text, index) => [
              <StatusCircle text={text} index={index} currentIndex={pageNumber} key={index + '-1'} />,
              index + 1 != pageNames.length ? (
                <Stack
                  height={34}
                  sx={{
                    width: 0.12,
                  }}
                  justifyContent="center"
                  key={index + '-2'}
                >
                  <CustomDivider
                    mainColor="#D9D9D9"
                    progressColor="primary.main"
                    progress={(() => {
                      if (index < pageNumber) return 100;
                      if (index === pageNumber) return currentPageProgress === undefined ? 30 : currentPageProgress;
                      return 0;
                    })()}
                  />
                </Stack>
              ) : (
                ''
              ),
            ])
            .flat()}
        </Stack>
      ) : (
        <Box
          sx={{
            backgroundColor: 'white',
            position: 'sticky',
            zIndex: 100,
            top: 0,
            boxSizing: 'border-box',
          }}
        >
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            py={3}
            px={pageTitle === 'demographics' ? 5 : 3}
            columnGap={1}
          >
            <Grid item xs={9.5} sx={{width: '100%'}}>
              <ResponsiveBar
                mainColor="#F6F5F5"
                progressColor="linear-gradient(115.11deg, #3DA9F8 7.09%, #35C6C9 65.83%, #A6E177 99.06%)"
                progress={useCurrentPageProgress ? currentPageProgress : possibleProgress}
              />
            </Grid>
            <Grid item>
              <Toolbar sx={{p: 0, justifyContent: 'flex-end'}}>
                <TriggerButton
                  iconButtonStyles={{
                    mr: 0.5,
                    p: 0,
                  }}
                />
              </Toolbar>
            </Grid>
          </Grid>
        </Box>
      )}
      <PricingPlansPopup open={paymentsPopupOpen} setOpen={setPaymentsPopupOpen} />
      {/* Modal dialog box when user clicks upgrade to premium button */}
      <InformativeDialog informativeDialog={informativeDialog} setInformativeDialog={setInformativeDialog} />
    </Box>
  );
};

/**
 * Footer component
 * Bottom side of the FormBase component, it will show 2 buttons, one for going next and other one to cancel or go back and an AIMD icon centered between those
 *
 * @param {Object} props - Component props
 * @param {function} props.handleNext - what happens when the user clicks the go next button
 * @param {function} props.handleBack - what happens when the user clicks the go back button
 * @param {string} props.backButtonName - Text rendered in the footer's back button
 * @param {string} props.nextButtonName - Text rendered in the footer's next button
 *
 * @returns {React.Component} Rendered Component
 */
const Footer = ({handleNext, handleBack, backButtonName, backButtonIcon, nextButtonName, nextButtonIcon}) => {
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const textStyles = {
    fontSize: 14,
    fontWeight: 500,
    width: 'fit-content',
  };

  return (
    <Stack
      key={'footer'}
      direction="row"
      justifyContent="space-between"
      sx={{
        p: 3,
        py: 2,
        position: 'sticky',
        bottom: 0,
        zIndex: 100,
        backgroundColor: '#FBFBFB',
      }}
    >
      <Button
        onClick={handleBack}
        disabled={backButtonName === ''}
        startIcon={isSmallScreen == false && (backButtonIcon ?? <ArrowBackIcon />)}
      >
        <Typography sx={textStyles} textAlign="left">
          {backButtonName}
        </Typography>
      </Button>
      <img
        src={mainLogo}
        alt="AIMD logo"
        height={50}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%,-50%)',
          userSelect: 'none',
        }}
      />
      <Button endIcon={isSmallScreen == false && (nextButtonIcon ?? <ArrowForwardIcon />)} onClick={handleNext}>
        <Typography sx={textStyles} textAlign="right">
          {nextButtonName}
        </Typography>
      </Button>
    </Stack>
  );
};

/**
 * FormBase
 * Template Layout and styles for a page that will be part of a form in the WebApp. This is meant to be used
 * to establish the basic settings that a multi-page form will have. Therefore, it is intended that other "base" components
 * are going to be created from this file, those components being the root of their forms.
 *
 * @param {Object} props - Component props
 * @param {string[]} props.pageNames - This component will contain a header which will show a progress bar based on this prop
 * @param {React.ReactNode} props.children
 * @param {number} props.pageNumber - Index within the props.pageNames array to show in which current page you are, this will affect the rendering of the header's progres bar
 * @param {function} props.handleNext - How this component works is that it will display a button to go back and another to go next,both buttons will be on the footer, this will handle what happens when the user clicks the next button
 * @param {function} props.handleBack - How this component works is that it will display a button to go back and another to go next,both buttons will be on the footer, this will handle what happens when the user clicks the back button
 * @param {string} props.backButtonName - Text rendered in the footer's back button
 * @param {string} props.nextButtonName - Text rendered in the footer's next button
 * @param {string || React.ReactNode} props.nextButtonIcon - Icon to be shown at the right of the next button(by default will be a forward arrow)
 * @param {string || React.ReactNode} props.backButtonIcon - Icon to be shown at the left of the back button(by default will be a back arrow)
 * @param {number} props.currentPageProgress - pass through prop that will go directly into the Header component
 * @param {boolean} props.useCurrentPageProgress - pass through prop that will go directly into the Header component
 * @param {boolean} props.dontAnimate - By default this component will try to create transitions when going to another page, this prop will disable that
 *
 * @returns {React.Component} Rendered Component
 */
export const FormBase = ({
  pageNames,
  children,
  pageNumber,
  handleNext,
  handleBack,
  backButtonName,
  backButtonIcon,
  nextButtonName,
  nextButtonIcon,
  currentPageProgress,
  useCurrentPageProgress,
  dontAnimate,
  pageTitle,
}) => {
  const {animationDirection, setAnimationDirection} = useContext(MiscContext);
  const containerRef = useRef(null);
  const [justMounted, setJustMounted] = useState(true);

  useEffect(() => {
    if (dontAnimate) return;
    //important so handleNext or back doesn't repeat more than once
    if (justMounted) return;

    if (animationDirection === 'left') {
      handleNext();
    }
    if (animationDirection === 'right') {
      handleBack();
    }
  }, [animationDirection, dontAnimate, handleBack, handleNext, justMounted]);

  const handleNextAnimation = () => {
    // this is assuming that the animation direction was already set up but there's an error message that is not letting the user advance(not a bug)
    if (!justMounted || dontAnimate) {
      handleNext();
    }
    setAnimationDirection('left');
    setJustMounted(false);
  };

  const handleBackAnimation = () => {
    // this is assuming that the animation direction was already set up but there's an error message that is not letting the user advance(not a bug)
    if (!justMounted || dontAnimate) handleBack();
    setAnimationDirection('right');
    setJustMounted(false);
  };

  return (
    <Stack minHeight="100vh" justifyContent="space-between">
      <Box ref={containerRef}>
        <Header
          pageNumber={pageNumber}
          pageNames={pageNames}
          currentPageProgress={currentPageProgress}
          useCurrentPageProgress={useCurrentPageProgress || false}
          pageTitle={pageTitle}
        />
        <Box>
          <Slide
            direction={animationDirection === '' ? 'left' : animationDirection}
            in={true}
            appear={dontAnimate ? false : true}
            container={containerRef.current}
          >
            <Paper sx={{boxShadow: 'none'}}>
              <Stack
                flexGrow={1}
                key="body"
                alignItems="center"
                justifyContent="flex-start"
                sx={{
                  marginX: {
                    xs: 3,
                    sm: 5,
                    md: 10,
                    lg: 10,
                  },
                  marginTop: {
                    xs: 0,
                    sm: 1,
                    md: 2,
                  },
                  boxSizing: 'border-box',
                }}
              >
                {children}
              </Stack>
            </Paper>
          </Slide>
        </Box>
      </Box>
      <Footer
        handleNext={handleNextAnimation}
        handleBack={handleBackAnimation}
        backButtonName={backButtonName}
        backButtonIcon={backButtonIcon}
        nextButtonName={nextButtonName}
        nextButtonIcon={nextButtonIcon}
      />
    </Stack>
  );
};
